import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  CostCenterSummary,
  Identity,
  OrderRequestStateEnum,
  OrderRowSupplyEntry,
  QuoteService,
  QuoteSummary,
} from '@libs/core/api';
import { ModalController } from '@ionic/angular';
import { QuoteSupplyCreateComponent } from '../quote-supply-create/quote-supply-create.component';
import { BehaviorSubject, take } from 'rxjs';
import { tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AnimationsService } from '../../services';

@UntilDestroy()
@Component({
  selector: 'ui-quote-supply-entries',
  templateUrl: './quote-supply-entries.component.html',
  styleUrls: ['./quote-supply-entries.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteSupplyEntriesComponent {
  @Input() identity!: Identity;
  @Input() project!: CostCenterSummary;

  private _detail!: QuoteSummary;
  @Input()
  set detail(value: QuoteSummary) {
    this._detail = value;
    this.updateSupplyEntries();
  }
  get detail(): QuoteSummary {
    return this._detail;
  }

  rowIndex = 0; //TODO: Muli-row support is not to be implemented yet

  constructor(
    private modalController: ModalController,
    private quoteService: QuoteService,
    private animationService: AnimationsService
  ) {}

  supplyEntries = new BehaviorSubject<OrderRowSupplyEntry[]>([]);

  updateDetail(): void {
    const id = this.project.id as number;
    const quoteId = this.detail?.id as number;

    this.quoteService
      .quoteRetrive({
        id,
        quoteId,
      })
      .pipe(
        take(1),
        tap((quote: QuoteSummary) => {
          this.detail = { ...this.detail, ...quote };
          this.updateSupplyEntries();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private updateSupplyEntries() {
    const entries =
      this.detail?.orderRequest?.rows?.[this.rowIndex]?.orderRowSupplyEntries ??
      [];
    this.supplyEntries.next(entries);
  }

  /** Supply entries can be added/edited when nothing is released yet and there is remaining amount. */
  get canEditSupplyEntries(): boolean {
    const releasedAmount = this.detail?.orderRequest?.releasedAmount ?? 0;
    const remainingAmount = this.detail?.orderRequest?.remainingAmount;
    return (
      releasedAmount <= 0 && remainingAmount != null && remainingAmount > 0
    );
  }

  get isRemainingAmountWarningActive(): boolean {
    if (!this.isSupplyEntryEnabled) {
      return false;
    }

    return !this.canEditSupplyEntries;
  }

  get isSupplyEntryEnabled() {
    return this.detail?.orderRequest?.state
      ? this.detail.orderRequest.state === OrderRequestStateEnum.$50
      : false;
  }

  get supplyEntriesQuantity() {
    const sum = this.supplyEntries.value.reduce((acc, entry) => {
      return acc + (entry.quantity ?? 0);
    }, 0);
    return sum;
  }

  get totalQuantity() {
    return this.orderRowQuantity ?? '-';
  }

  get orderRowQuantity(): number | undefined {
    return this.detail?.orderRequest?.rows?.[this.rowIndex]?.quantity;
  }

  async addSupplyEntry() {
    const modal = await this.modalController.create({
      component: QuoteSupplyCreateComponent,
      mode: 'md',
      componentProps: {
        identity: this.identity,
        detail: this.detail,
        maxOrders: this.missingOrders,
        disableDelete: !this.canEditSupplyEntries,
      },
      enterAnimation: this.animationService.slideLeftToRight,
      leaveAnimation: this.animationService.slideRightToLeft,
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data === 'success') {
      this.updateDetail();
    }
  }

  async openDetail(supplyEntry: OrderRowSupplyEntry) {
    const modal = await this.modalController.create({
      component: QuoteSupplyCreateComponent,
      mode: 'md',
      componentProps: {
        identity: this.identity,
        detail: this.detail,
        supplyEntry: supplyEntry,
        disableDelete: !this.canEditSupplyEntries,
      },
      enterAnimation: this.animationService.slideLeftToRight,
      leaveAnimation: this.animationService.slideRightToLeft,
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.updateDetail();
    }
  }

  get isLimitReached(): boolean {
    const max = this.orderRowQuantity;
    return max != null && this.supplyEntriesQuantity >= max;
  }

  get missingOrders() {
    return (this.orderRowQuantity ?? 0) - this.supplyEntriesQuantity;
  }
}
